import { computed, reactive } from 'vue';

// The open sidebar folders are kept in local storage, so they stay open after a
// reload and in a new window. Storage can be blocked, hence the try/catch.
const EXPANDED_MENU_ITEMS_KEY = 'sidebar.expandedMenuItems';

function readExpandedMenuItems() {
    try {
        const stored = JSON.parse(localStorage.getItem(EXPANDED_MENU_ITEMS_KEY));
        return Array.isArray(stored) ? stored : [];
    } catch (e) {
        return [];
    }
}

// Writes only this folder's state, so a folder opened or closed in another
// window is not overwritten.
function storeMenuItemExpanded(key, expanded) {
    try {
        const stored = new Set(readExpandedMenuItems());
        if (expanded) {
            stored.add(key);
        } else {
            stored.delete(key);
        }
        localStorage.setItem(EXPANDED_MENU_ITEMS_KEY, JSON.stringify([...stored]));
    } catch (e) {
        // The folder then stays open in this page only.
    }
}

const layoutConfig = reactive({
    preset: 'Aura',
    primary: 'emerald',
    surface: null,
    darkTheme: false,
    menuMode: 'static'
});

const layoutState = reactive({
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    profileSidebarVisible: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
    activeMenuItem: null,
    expandedMenuItems: new Set(readExpandedMenuItems())
});

export function useLayout() {
    const setActiveMenuItem = (item) => {
        layoutState.activeMenuItem = item.value || item;
    };

    const toggleMenuItem = (itemKey) => {
        const key = itemKey.value || itemKey;
        if (layoutState.expandedMenuItems.has(key)) {
            layoutState.expandedMenuItems.delete(key);
            storeMenuItemExpanded(key, false);
        } else {
            layoutState.expandedMenuItems.add(key);
            storeMenuItemExpanded(key, true);
        }
    };

    const isMenuItemExpanded = (itemKey) => {
        const key = itemKey.value || itemKey;
        return layoutState.expandedMenuItems.has(key);
    };

    const toggleDarkMode = () => {
        if (!document.startViewTransition) {
            executeDarkModeToggle();

            return;
        }

        document.startViewTransition(() => executeDarkModeToggle(event));
    };

    const executeDarkModeToggle = () => {
        layoutConfig.darkTheme = !layoutConfig.darkTheme;
        document.documentElement.classList.toggle('app-dark');
    };

    const toggleMenu = () => {
        if (layoutConfig.menuMode === 'overlay') {
            layoutState.overlayMenuActive = !layoutState.overlayMenuActive;
        }

        if (window.innerWidth > 991) {
            layoutState.staticMenuDesktopInactive = !layoutState.staticMenuDesktopInactive;
        } else {
            layoutState.staticMenuMobileActive = !layoutState.staticMenuMobileActive;
        }
    };

    const isSidebarActive = computed(() => layoutState.overlayMenuActive || layoutState.staticMenuMobileActive);

    const isDarkTheme = computed(() => layoutConfig.darkTheme);

    const getPrimary = computed(() => layoutConfig.primary);

    const getSurface = computed(() => layoutConfig.surface);

    return {
        layoutConfig,
        layoutState,
        toggleMenu,
        isSidebarActive,
        isDarkTheme,
        getPrimary,
        getSurface,
        setActiveMenuItem,
        toggleMenuItem,
        isMenuItemExpanded,
        toggleDarkMode
    };
}
